<template>
  <div id="app" class="app" v-if="isReady && !isLoading">
    <menu-comp></menu-comp>
    <transition
      mode="out-in"
      v-on:enter="enter"
      v-on:leave="leave"
      v-bind:css="false"
    >
      <router-view></router-view>
    </transition>
  </div>
</template>

<script>
import 'styles/base/index.styl'
import { mapGetters, mapActions } from 'vuex'
import * as PIXI from 'pixi.js'
import Emitter from 'core/Emitter'
import Detector from 'utils/Detector'
import Stats from 'stats.js'
import SoundManager from 'components/SoundManager'
import MenuComp from 'components/UI/Menu.vue'

export default {
  name: 'app',
  data () {
    return {
      fakeScroll: null,
      isReady: false,
      isLoading: true
    }
  },
  components: {
    MenuComp
  },
  computed: {
    // ...mapGetters({
    //   deviceInformations: 'deviceInformations',
    //   lightSensitivity: 'deviceLightSensitivity',
    //   isDark: 'isDark'
    // }),
  },
  created () {
    this.scrollVars = {
      ease: (Detector.isMobile()) ? 0.15 : 0.1,
      last: 0,
      current: 0,
      target: 0
    }

    this.loadJson()
  },
  mounted () {
    this.bindAll()
  },
  beforeDestroy () {
    this.removeListeners()
    this.removeFakeScroll()
    // this.removeFpsMeter()

    window.loaderManager.loader.reset()

    if (this.soundManager) {
      this.soundManager.destroy()
      this.soundManager = null
    }

    this.resources = {}
  },

  methods: {
    // ...mapActions([
    //   'setChapter',
    //   'setDeviceInformations',
    //   'setLightSensitivity',
    //   'setIsDark'
    // ]),
    bindAll () {
      ['update', 'resize', 'scroll', 'openSinglePlant', 'checkFocus']
      .forEach((fn) => (this[fn] = this[fn].bind(this)))
    },

    loadJson () {
      let loader = new PIXI.loaders.Loader()
      loader.reset()

      loader.add('flowers', '/static/assets/data/flowers.json')
      loader.load((loader, resources) => {
        this.flowers = resources['flowers']
        this.flowersData = resources['flowers'].data

        let files = [
          {name: 'spriteMap', url: '/static/assets/sounds/spriteMap.json'},
          {name: 'soundSprites', url: '/static/assets/sounds/soundsprites.mp3', xhrType: PIXI.loaders.Resource.XHR_RESPONSE_TYPE.BLOB, loadType: PIXI.loaders.Resource.LOAD_TYPE.XHR},
          {name: 'little_particle', url: '/static/assets/img/particule_A_02.png'},
          {name: 'threshold', url: '/static/assets/img/threshold.jpg'}
        ]
        window.loaderManager.addFiles(files)

        this.loadResources()
      })
    },

    loadResources () {
      if (this.oldRoute === this.$route.name) return

      this.isLoading = true

      let files = [
        {name: 'mask_1', url: '/static/assets/img/mask_1.png'},
        {name: 'home_logo_jardins', url: '/static/assets/img/logo_jardin.png'},
        {name: 'home_logo_rmn', url: '/static/assets/img/home/home_logo_rmn.png'},
        {name: 'homer_cover_jardins', url: '/static/assets/img/home/homer_cover_jardins.jpg'},
        {name: 'secrets_vertus_plantes', url: '/static/assets/img/home/secrets_vertus_plantes@2x.png'},
        {name: 'home_left_plant', url: '/static/assets/img/home/home_left_plant.png'},
        {name: 'home_right_plant', url: '/static/assets/img/home/home_right_plant.png'},
        {name: '13_Souci_Oeuvre_02', url: '/static/assets/img/plantes/13_Souci/13_Souci_Oeuvre_02.jpg'},
        {name: 'BG_fleur', url: '/static/assets/img/home/BG_fleur.jpg'}
      ]

      this.flowersData.forEach((flower) => {
        files.push({name: flower.title, url: `/static/assets/img/spritesheets/${flower.id}_${flower.title}.json`})
      })

      window.loaderManager.addFiles(files)

      window.loaderManager.loadAll(() => {
        Object.assign(window.loaderManager.resources, {flowers: this.flowers})

        if (!this.firstInit) {
          this.init()
        }

        this.isLoading = false
      })
    },

    init () {
      this.soundSprite = window.URL.createObjectURL(window.loaderManager.resources['soundSprites'].data)
      this.spriteMap = window.loaderManager.resources['spriteMap'].data

      this.globalTime = Math.random() * 10000
      this.firstInit = true
      this.isReady = true
      // this.addFpsMeter()
      this.addFakeScroll()
      this.addListeners()
      this.soundManager = new SoundManager({
        soundSprites: this.soundSprite,
        spriteMap: this.spriteMap
      })
      if (!this.requestId) {
        this.update()
      }
    },

    addListeners () {
      Emitter.on('OPEN_SINGLE', this.openSinglePlant)
      Emitter.on('GET_RESIZE', this.resize)

      window.addEventListener('scroll', this.scroll)
      let visibilityChange
      if (typeof document.hidden !== 'undefined') {
        visibilityChange = 'visibilitychange'
      } else if (typeof document.mozHidden !== 'undefined') {
        visibilityChange = 'mozvisibilitychange'
      } else if (typeof document.msHidden !== 'undefined') {
        visibilityChange = 'msvisibilitychange'
      } else if (typeof document.webkitHidden !== 'undefined') {
        visibilityChange = 'webkitvisibilitychange'
      }

      document.addEventListener(visibilityChange, this.checkFocus)
      window.addEventListener('focus', this.checkFocus)
      window.addEventListener('blur', this.checkFocus)

      if (Detector.isMobile()) {
        window.addEventListener('orientationchange', this.resize)
      } else {
        window.addEventListener('resize', this.resize)
      }
    },
    removeListeners () {
      Emitter.removeListener('OPEN_SINGLE', this.openSinglePlant)
      Emitter.removeListener('GET_RESIZE', this.resize)

      window.removeEventListener('scroll', this.scroll)

      if (Detector.isMobile()) {
        window.removeEventListener('orientationchange', this.resize)
      } else {
        window.removeEventListener('resize', this.resize)
      }

      if (this.requestId) {
        window.cancelAnimationFrame(this.requestId)
        this.requestId = undefined
      }
    },

    checkFocus () {
      Emitter.emit('SOUND::TOGGLE_SOUND', {muted: document.hasFocus(), sender: 'checkFocus'})
    },

    addFpsMeter () {
      this.meter = new Stats()
      this.meter.dom.classList.add('fps-meter')
      this.meter.dom.style.left = ''
      this.meter.dom.style.right = '0px'
      document.body.appendChild(this.meter.dom)
    },

    removeFpsMeter () {
      document.body.removeChild(this.meter.dom)
    },

    resize () {
      this.size = Detector.getScreenSize()

      Emitter.emit('RESIZE', {
        size: this.size
      })
    },

    scroll () {
      this.scrollVars.target = window.pageYOffset

      Emitter.emit('SCROLL', {
        scrollVars: this.scrollVars
      })

      this.scrollVars.last = this.scrollVars.target
    },

    update () {
      // this.meter.begin()
      this.globalTime += 0.01

      this.scrollVars.current += (this.scrollVars.target - this.scrollVars.current) * this.scrollVars.ease
      this.scrollVars.current < 0.1 && (this.scrollVars.current = 0)

      Emitter.emit('TICK', {
        scrollVars: this.scrollVars,
        globalTime: this.globalTime
      })

      // this.meter.end()
      this.requestId = window.requestAnimationFrame(this.update)
    },

    addFakeScroll () {
      this.fakeScroll = document.createElement('div')
      this.fakeScroll.classList.add('fake-scroll')
      document.body.appendChild(this.fakeScroll)
    },

    removeFakeScroll () {
      document.body.removeChild(this.fakeScroll)
      this.fakeScroll = null
    },

    getSinglePlant (slug) {
      return new Promise((resolve, reject) => {
        let arr = this.flowersData.filter(function (item) {
          let slugItem = item.title.toLowerCase()
          return (slug === slugItem)
        })

        let item = arr[0]
        let index = this.flowersData.indexOf(item)

        let previousIndex = Math.max(-1, index - 1)
        if (previousIndex === -1) {
          previousIndex = this.flowersData.length - 1
        }
        let previousSlug = this.flowersData[previousIndex].title.toLowerCase()

        let nextIndex = Math.min(index + 1, this.flowersData.length - 1)
        if (nextIndex === this.flowersData.length - 1) {
          nextIndex = 0
        }
        let nextSlug = this.flowersData[nextIndex].title.toLowerCase()

        if (item) {
          resolve({
            plant: item,
            nav: {
              prev: previousSlug,
              next: nextSlug
            }
          })
        } else {
          reject({
            error: {
              'code': '404',
              'msg': 'La plante n\' existe pas. '
            }
          })
        }
      })
    },
    openSinglePlant (title) {
      let slug = title.toLowerCase()
      this.$router.push({
        name: 'single-plant',
        params: {
          slug: slug
        }
      })
    },
    enter (el, done) {
      if (el.className === 'single-plant') {
        this.enterSinglePlant(el, done)
      } else if (el.className === 'home') {
        this.enterHome(el, done)
      } else {
        window.scrollTo(0, 0)
        this.scrollVars.current = 0
        done()
      }
    },
    leave (el, done) {
      if (el.className === 'single-plant') {
        this.leaveSinglePlant(el, done)
      } else if (el.className === 'home') {
        this.leaveHome(el, done)
      } else {
        this.leaveAbout(el, done)
      }
    },
    enterHome (el, done) {
      done()
    },
    enterSinglePlant (el, done) {
      done()
    },
    leaveHome (el, done) {
      window.scrollTo(0, 0)
      this.scrollVars.current = 0
      TweenMax.to(el, 0.6, {
        opacity: 0,
        ease: Expo.easeOut,
        onComplete: () => {
          this.scene.destroy()
          this.scene = null
          done()
        }
      })
    },
    leaveSinglePlant (el, done) {
      let canvasContainer = el.querySelector('.single-plant__canvas-container')
      let tl = new TimelineMax({
        onComplete: () => {
          this.scene.destroy()
          this.scene = null
          done()
        }
      })
      tl.to(canvasContainer, 0.3, {
        autoAlpha: 0,
        ease: Expo.easeOut
      })
      .to(el, 0.6, {
        autoAlpha: 0,
        ease: Expo.easeOut
      })
    },
    leaveAbout (el, done) {
      window.scrollTo(0, 0)
      this.scrollVars.current = 0
      TweenMax.to(el, 1, {
        opacity: 0,
        ease: Expo.easeOut,
        onComplete: () => {
          done()
        }
      })
    }
  }
}
</script>



/** WEBPACK FOOTER **
 ** App.vue?38d80abc
 **/